import { useState } from "react";
import type { ReactNode } from "react";
import { useForm } from "react-hook-form";

import Button from "../../atomic/Button";
import Input from "../FormElements/Input";
import { LoanApplicationService } from "../../../services/LoanApplication";

/**
 * Matches the host app's `notify` from its own AlertContext.
 *
 * This component is rendered by the consuming dashboard (platform / lending),
 * and each of those mounts its <Toaster /> from its own react-hot-toast copy.
 * This tree has a separate copy, so a toast raised from here would land in a
 * store nothing renders — the host has to pass its own notify in.
 */
type Notify = (args: {
  title: ReactNode;
  text?: ReactNode;
  type?: "success" | "error" | "info";
}) => void;

interface IProps {
  userID: string;
  token: string;
  pageName: string;
  notify: Notify;
  setOpen: Function;
  reload?: Function;
}

type FormInputs = {
  reason: string;
};

export default function BypassBREModal({
  userID,
  token,
  pageName,
  notify,
  setOpen,
  reload
}: IProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormInputs>({
    defaultValues: {
      reason: ""
    }
  });

  async function onSubmit(data: FormInputs) {
    setIsSubmitting(true);
    const response = await LoanApplicationService.bypassBRE({
      userID,
      token,
      pageName,
      reason: data.reason
    });
    setIsSubmitting(false);

    if (response.status) {
      notify({
        title: "Success",
        text: "BRE bypassed successfully",
        type: "success"
      });

      reload?.();
    } else {
      notify({
        title: "Error",
        text: response.error
      });
    }
    setOpen(false);
  }

  return (
    <>
      <div className="sm:flex sm:items-start">
        <div className="mt-3 text-center sm:ml-3 sm:mt-0 sm:text-left">
          <div className="mt-2">
            <p className="text-sm text-gray-500">
              Are you sure you want to bypass the BRE?
            </p>
          </div>
        </div>
      </div>
      <form className="flex flex-col mt-4" onSubmit={handleSubmit(onSubmit)}>
        <fieldset className="mb-4 px-2">
          <Input
            type="text"
            placeholder="Reason for bypassing BRE"
            register={register("reason", {
              required: true
            })}
            error={errors.reason}
            errorText={errors?.reason?.type === "required" ? "required" : ""}
          />
        </fieldset>
        <fieldset className="flex items-center justify-end">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-sm px-4 py-2 w-auto rounded-lg bg-white border border-gray-300 text-gray-700 flex items-center justify-center font-medium "
          >
            Cancel
          </button>
          <Button
            type="submit"
            color="primary"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            className="inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto"
          >
            Confirm
          </Button>
        </fieldset>
      </form>
    </>
  );
}
